import { FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import styles from "./styles.module.scss";

/**
 * Wybór okresu w dwóch kolumnach:
 *  - „Wskaż okres" — Rok + Okres od + Okres do (dowolny przedział, pionowo),
 *  - „Predefiniowane" — gotowe zakresy względem dziś (pionowo).
 * Komponent sterowany: całość stanu (rok/od/do/preset) trzyma rodzic.
 */

const pad2 = (n: number) => String(n).padStart(2, "0");
const toISO = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const todayISO = () => toISO(new Date());

type PresetKey =
  | "biezacyMiesiac"
  | "poprzedniMiesiac"
  | "biezacyKwartal"
  | "poprzedniKwartal"
  | "biezacyRok";

const PRESETS: { key: PresetKey; label: string }[] = [
  { key: "biezacyMiesiac", label: "Bieżący miesiąc" },
  { key: "poprzedniMiesiac", label: "Poprzedni miesiąc" },
  { key: "biezacyKwartal", label: "Bieżący kwartał" },
  { key: "poprzedniKwartal", label: "Poprzedni kwartał" },
  { key: "biezacyRok", label: "Bieżący rok" },
];

// Zakres [od, do, rok] dla predefiniowanej opcji — względem dziś.
const rangeFor = (key: PresetKey): [string, string, number] => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const q = Math.floor(m / 3);
  switch (key) {
    case "biezacyMiesiac":
      return [toISO(new Date(y, m, 1)), todayISO(), y];
    case "poprzedniMiesiac": {
      const s = new Date(y, m - 1, 1);
      const e = new Date(y, m, 0); // ostatni dzień poprzedniego miesiąca
      return [toISO(s), toISO(e), s.getFullYear()];
    }
    case "biezacyKwartal":
      return [toISO(new Date(y, q * 3, 1)), todayISO(), y];
    case "poprzedniKwartal": {
      const s = new Date(y, (q - 1) * 3, 1); // ujemny miesiąc → poprzedni rok
      const e = new Date(y, q * 3, 0);
      return [toISO(s), toISO(e), s.getFullYear()];
    }
    case "biezacyRok":
    default:
      return [toISO(new Date(y, 0, 1)), todayISO(), y];
  }
};

interface Props {
  year: number;
  from: string;
  to: string;
  preset: string | null;
  yearOptions: number[];
  setYear: (y: number) => void;
  setFrom: (v: string) => void;
  setTo: (v: string) => void;
  setPreset: (p: string | null) => void;
}

const PeriodPicker = ({
  year, from, to, preset, yearOptions,
  setYear, setFrom, setTo, setPreset,
}: Props) => {
  const applyPreset = (key: PresetKey) => {
    const [f, t, yy] = rangeFor(key);
    setFrom(f);
    setTo(t);
    setYear(yy);
    setPreset(key);
  };

  // Zmiana roku ustawia cały rok (dowolny przedział, bez predefiniowanej opcji).
  const handleYear = (yy: number) => {
    setYear(yy);
    setPreset(null);
    setFrom(`${yy}-01-01`);
    setTo(yy === new Date().getFullYear() ? todayISO() : `${yy}-12-31`);
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.column}>
        <div className={styles.colHeader}>Wskaż okres</div>
        <FormControl size="small">
          <InputLabel>Rok</InputLabel>
          <Select
            label="Rok"
            value={year}
            onChange={(e) => handleYear(Number(e.target.value))}
            MenuProps={{ sx: { zIndex: 1500 } }}
          >
            {yearOptions.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField
          label="Okres od"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={from}
          onChange={(e) => { setFrom(e.target.value); setPreset(null); }}
        />
        <TextField
          label="Okres do"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={to}
          onChange={(e) => { setTo(e.target.value); setPreset(null); }}
        />
      </div>

      <div className={styles.divider} />

      <div className={styles.column}>
        <div className={styles.colHeader}>Predefiniowane</div>
        {PRESETS.map((p) => (
          <button
            key={p.key}
            className={`${styles.presetBtn} ${preset === p.key ? styles.presetBtnActive : ""}`}
            onClick={() => applyPreset(p.key)}
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default PeriodPicker;
